/**
 * @fileoverview PWA installation hook.
 * @summary Custom hook for managing PWA installation prompt and status.
 *
 * @package KaraoNow
 * @module apps/front/src/hooks/usePWA
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useState, useEffect, useCallback } from 'react'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

interface UsePWAResult {
  /** Whether the app can be installed */
  canInstall: boolean
  /** Whether the app is already installed */
  isInstalled: boolean
  /** Whether the app is running as a PWA (standalone) */
  isStandalone: boolean
  /** Trigger the install prompt */
  installPrompt: () => Promise<boolean>
  /** Whether the service worker is ready */
  isReady: boolean
}

/**
 * @summary Custom hook for PWA installation management.
 * @returns {UsePWAResult} PWA state and install function.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export function usePWA(): UsePWAResult {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(false)
  const [isReady, setIsReady] = useState(false)

  // Check if running in standalone mode (already installed as PWA)
  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true)

  useEffect(() => {
    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true)
      setDeferredPrompt(null)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    // Check if service worker is ready
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(() => {
        setIsReady(true)
      })
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const installPrompt = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) {
      return false
    }

    try {
      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice

      if (outcome === 'accepted') {
        setIsInstalled(true)
        setDeferredPrompt(null)
        return true
      }
    } catch (error) {
      console.error('Error showing install prompt:', error)
    }

    return false
  }, [deferredPrompt])

  return {
    canInstall: !!deferredPrompt && !isStandalone,
    isInstalled: isInstalled || isStandalone,
    isStandalone,
    installPrompt,
    isReady,
  }
}

export default usePWA

